import { Link } from "react-router-dom";

import type { K, OzetResponse } from "@/api/ozet";
import type { ReactNode } from "react";
import type { DeltaMode } from "@/hooks/useDeltaMode";
import { t, type TrKey } from "@/i18n";
import { fmtInt, fmtMoney } from "@/lib/utils";

import { fmtGun, fmtPctAlready, fmtPuan, fmtX, hukumToVerdictLevel } from "./format";

/**
 * Gidisat hero — 4 blok (satis/pazarlama/musteri/operasyon), her biri 4
 * metrik + tek satirlik hukum; genel hukum ustte serit. Tasarim (17.09.2026):
 * blok basina buyuk renkli kutu YOK — hukum renkli nokta + tek satir; reklam
 * baglantisi yoksa Pazarlama blogu dort tire yerine tek satir + ayar linki.
 * `deltaMode` sayfadan PROP olarak gelir (Δ secici bolum basliginda).
 */

type Fmt = (v: number | null | undefined) => string;

interface MetricDef {
  labelKey: TrKey;
  kind: "k" | "plain";
  fmt: Fmt;
  goodUp?: boolean;
}

interface BlockDef {
  key: "satis" | "pazarlama" | "musteri" | "operasyon";
  icon: string;
  titleKey: TrKey;
  link: string;
  metrics: Array<MetricDef & { get: (d: OzetResponse) => K | number | null | undefined }>;
}

const BLOCKS: BlockDef[] = [
  {
    key: "satis",
    icon: "🛒",
    titleKey: "ozet.blok.satis",
    link: "/kpi",
    metrics: [
      { labelKey: "ozet.metrik.ciro", kind: "k", fmt: fmtMoney, get: (d) => d.satis.ciro },
      { labelKey: "ozet.metrik.siparis", kind: "k", fmt: fmtInt, get: (d) => d.satis.siparis },
      { labelKey: "ozet.metrik.sepet_ortalama", kind: "k", fmt: fmtMoney, get: (d) => d.satis.aov },
      { labelKey: "ozet.metrik.yeni_musteri", kind: "k", fmt: fmtInt, get: (d) => d.satis.yeni_musteri },
    ],
  },
  {
    key: "pazarlama",
    icon: "📣",
    titleKey: "ozet.blok.pazarlama",
    link: "/kpi",
    metrics: [
      { labelKey: "ozet.metrik.reklam_harcama", kind: "k", fmt: fmtMoney, goodUp: false, get: (d) => d.pazarlama.harcama },
      { labelKey: "ozet.metrik.roas", kind: "k", fmt: fmtX, get: (d) => d.pazarlama.roas },
      { labelKey: "ozet.metrik.mer", kind: "k", fmt: fmtX, get: (d) => d.pazarlama.mer },
      { labelKey: "ozet.metrik.reklam_maliyet_orani", kind: "k", fmt: fmtPctAlready, goodUp: false, get: (d) => d.pazarlama.reklam_maliyet_orani_pct },
    ],
  },
  {
    key: "musteri",
    icon: "⭐",
    titleKey: "ozet.blok.musteri",
    link: "/yorumlar",
    metrics: [
      { labelKey: "ozet.metrik.yorum", kind: "k", fmt: fmtInt, get: (d) => d.musteri.yorum_adet },
      { labelKey: "ozet.metrik.ortalama_puan", kind: "k", fmt: fmtPuan, get: (d) => d.musteri.ort_puan },
      { labelKey: "ozet.metrik.dusuk_puan", kind: "k", fmt: fmtInt, goodUp: false, get: (d) => d.musteri.dusuk_puan_adet },
      { labelKey: "ozet.metrik.destek_bekleyen", kind: "plain", fmt: fmtInt, get: (d) => d.musteri.destek_bekleyen },
    ],
  },
  {
    key: "operasyon",
    icon: "🚚",
    titleKey: "ozet.blok.operasyon",
    link: "/stok",
    metrics: [
      { labelKey: "ozet.metrik.teslim_suresi", kind: "k", fmt: fmtGun, goodUp: false, get: (d) => d.operasyon.teslim_gun },
      { labelKey: "ozet.metrik.kargolanmamis", kind: "plain", fmt: fmtInt, get: (d) => d.operasyon.kargolanmamis },
      { labelKey: "ozet.metrik.yolda_14g", kind: "plain", fmt: fmtInt, get: (d) => d.operasyon.yolda_14g_ustu },
      { labelKey: "ozet.metrik.kritik_stok", kind: "plain", fmt: fmtInt, get: (d) => d.operasyon.kritik_stok },
    ],
  },
];

/**
 * Δ rozeti — mod "pct" iken %fark; mod "value" iken cur−prev farki `fmt` ile.
 * Sayfa disindan da kullanilir (HarcamaEtkisi, SiteMetrikleri).
 */
export function DeltaBadge({ k, fmt, goodUp = true, mode = "pct" }: { k: K; fmt: Fmt; goodUp?: boolean; mode?: DeltaMode }) {
  if (k.delta_pct == null) {
    if (k.cur != null && k.prev == null) {
      return <div className="text-[11px] text-muted">{t("ozet.onceki_donem_yok")}</div>;
    }
    return null;
  }
  const isUp = k.delta_pct >= 0;
  const isGood = goodUp ? isUp : !isUp;
  const cls = k.delta_pct === 0 ? "text-muted" : isGood ? "text-green" : "text-red";
  const arrow = k.delta_pct > 0 ? "▲" : k.delta_pct < 0 ? "▼" : "→";
  let mainTxt = `%${Math.abs(k.delta_pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}`;
  if (mode === "value" && k.cur != null && k.prev != null) {
    const diff = k.cur - k.prev;
    const sign = diff > 0 ? "+" : diff < 0 ? "−" : "";
    mainTxt = `${sign}${fmt(Math.abs(diff))}`;
  }
  return (
    <div className={`num text-xs font-semibold ${cls}`}>
      {arrow} {mainTxt}
      <span className="sr-only">
        {" "}
        ({fmt(k.prev)} → {fmt(k.cur)})
      </span>
    </div>
  );
}

function MetricCell({ metric, value, deltaMode }: { metric: MetricDef; value: K | number | null | undefined; deltaMode: DeltaMode }) {
  const label = t(metric.labelKey);
  if (metric.kind === "k") {
    const k = (value as K | undefined) ?? { cur: null, prev: null, delta_pct: null };
    return (
      <div className="min-w-0">
        <div className="text-xs text-muted">{label}</div>
        <div className="num text-lg font-bold leading-tight text-ink">{metric.fmt(k.cur)}</div>
        <DeltaBadge k={k} fmt={metric.fmt} goodUp={metric.goodUp} mode={deltaMode} />
      </div>
    );
  }
  const v = value as number | null | undefined;
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted">{label}</div>
      <div className="num text-lg font-bold leading-tight text-ink">{metric.fmt(v)}</div>
      {v != null && <div className="text-[11px] text-muted">{t("ozet.anlik_veri")}</div>}
    </div>
  );
}

const HUKUM_DOT: Record<"good" | "warn" | "bad", string> = { good: "bg-green", warn: "bg-amber", bad: "bg-red" };
const HUKUM_TEXT: Record<"good" | "warn" | "bad", string> = { good: "text-[#14532d]", warn: "text-[#78350f]", bad: "text-[#991b1b]" };

/** Blok hukmu — tek satir, renkli nokta (eski buyuk kutu yerine). */
function HukumSatiri({ cls, ikon, metin }: { cls: string; ikon: string; metin: string }) {
  const level = hukumToVerdictLevel(cls);
  return (
    <p role="status" className={`flex items-start gap-2 text-[13px] leading-snug ${HUKUM_TEXT[level]}`}>
      <span aria-hidden="true" className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${HUKUM_DOT[level]}`} />
      <span>
        <span className="sr-only">{ikon} </span>
        {metin}
      </span>
    </p>
  );
}

interface GidisatHeroProps {
  data: OzetResponse;
  deltaMode: DeltaMode;
  chart?: ReactNode;
  context?: ReactNode;
}

export function GidisatHero({ data, deltaMode, chart, context }: GidisatHeroProps) {
  const sales = BLOCKS[0];
  const salesError = data.satis.status === "error";
  const general = data.hukum.genel;
  return (
    <div data-testid="gidisat-hero" className="performance-layout">
      <div className="sales-panel" data-testid="gd-blok-satis">
        <div className="sales-heading"><h3>Satış performansı</h3><span className="text-xs text-muted">Seçili rapor dönemi</span>{salesError && <span className="text-xs text-red">{t("ozet.veri_yok")}</span>}</div>
        <div className="sales-revenue">
          <p className="text-sm text-muted">{t("ozet.metrik.ciro")}</p>
          <div className="num sales-revenue-value">{fmtMoney(salesError ? null : data.satis.ciro?.cur)}</div>
          {!salesError && data.satis.ciro && <div className="mt-2 flex flex-wrap items-center gap-2"><DeltaBadge k={data.satis.ciro} fmt={fmtMoney} mode={deltaMode} /><span className="text-xs text-muted">önceki eşit döneme göre</span></div>}
        </div>
        <div className="sales-secondary">{sales.metrics.slice(1).map(metric => <MetricCell key={metric.labelKey} metric={metric} value={salesError ? null : metric.get(data)} deltaMode={deltaMode} />)}</div>
        {chart && <div className="sales-chart">{chart}</div>}
        {general && <div className="sales-verdict"><HukumSatiri cls={general.cls} ikon={general.ikon} metin={general.metin} /></div>}
        {data.satis.definition && <details className="mt-3 text-xs text-muted"><summary className="cursor-pointer py-2">Ciro nasıl hesaplanıyor?</summary><p className="pb-2 leading-relaxed">{data.satis.definition}</p></details>}
        <Link to={sales.link} className="btn-link text-xs">Satış ve reklam ayrıntıları</Link>
        {data.satis.hedef && <div className="mt-4 border-t border-line pt-4" data-testid="gd-hedef"><h3 className="mb-3 text-sm font-semibold">{t("ozet.hedef.title")}</h3><div className="grid gap-4 sm:grid-cols-2">{data.satis.hedef.ciro && <HedefBar labelKey="ozet.metrik.ciro" hedef={data.satis.hedef.ciro} fmt={fmtMoney} />}{data.satis.hedef.siparis && <HedefBar labelKey="ozet.metrik.siparis" hedef={data.satis.hedef.siparis} fmt={fmtInt} />}</div></div>}
      </div>
      <aside className="performance-aside" aria-label="Diğer göstergeler">
        {context}
        <div className="supporting-metrics">
          <h3 className="mb-1 text-sm font-semibold text-ink">Diğer göstergeler</h3>
          <p className="mb-2 text-xs text-muted">Ayrıntılar için bir başlığı açın.</p>
          {BLOCKS.slice(1).map(block => {
            const section = (data as unknown as Record<string, { status?: string }>)[block.key];
            const disconnected = block.key === "pazarlama" && (section?.status === "not_connected" || section?.status === "error");
            const error = section?.status === "error";
            const h = data.hukum[block.key];
            return <div key={block.key} data-testid={`gd-blok-${block.key}`} className="supporting-row">
              {disconnected ? <div data-testid="gd-pazarlama-bagli-degil"><h4 className="text-sm font-semibold">{t(block.titleKey)}</h4><p className="mt-2 text-xs leading-relaxed text-muted">{section?.status === "error" ? t("ozet.pazarlama.hata") : t("ozet.pazarlama.bagli_degil")}</p><Link to="/ayarlar" className="btn-link mt-1 text-xs">{t("ozet.pazarlama.baglanti_link")}</Link></div> : <details>
                <summary className="metric-summary"><span className="font-semibold">{t(block.titleKey)}</span><span className="text-xs text-muted">Ayrıntı</span></summary>
                {error && <p className="mb-2 text-xs text-red">{t("ozet.veri_yok")}</p>}
                <div className="grid grid-cols-2 gap-x-3 gap-y-4 pb-3 pt-2">{block.metrics.map(metric => <MetricCell key={metric.labelKey} metric={metric} value={error ? null : metric.get(data)} deltaMode={deltaMode} />)}</div>
                {block.key === "pazarlama" && data.pazarlama.scope_note && <p className="text-xs leading-relaxed text-muted">{data.pazarlama.scope_note}</p>}
                {block.key === "operasyon" && <p className="text-xs leading-relaxed text-muted">14 gün+: kargoya verileli 14 gün geçmiş, henüz teslim edilmemiş siparişler. Takvim günü esas alınır; seçilen rapor döneminden bağımsızdır. {!!data.operasyon.kargo_tarihi_bilinmeyen && `${data.operasyon.kargo_tarihi_bilinmeyen} siparişin kargoya veriliş tarihi bilinmiyor; süre hesabına dahil değil.`}</p>}
                {block.key === "operasyon" ? <a href="#operasyon-ozeti" className="btn-link mt-2 text-xs">Operasyon ayrıntılarını incele</a> : <Link to={block.link} className="btn-link mt-2 text-xs">{t(block.titleKey)} ayrıntılarını incele</Link>}
              </details>}
              {!disconnected && h && <div className="mt-2"><HukumSatiri cls={h.cls} ikon={h.ikon} metin={h.metin} /></div>}
            </div>;
          })}
        </div>
      </aside>
    </div>
  );
}

function HedefBar({ labelKey, hedef, fmt }: { labelKey: TrKey; hedef: { hedef_donem: number; gerceklesen: number; oran_pct: number | null }; fmt: Fmt }) {
  const pct = hedef.oran_pct == null ? 0 : Math.max(0, Math.min(100, hedef.oran_pct));
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs text-muted">
        <span>{t(labelKey)}</span>
        <span className="num">
          {fmt(hedef.gerceklesen)} / {fmt(hedef.hedef_donem)} ({hedef.oran_pct != null ? `%${hedef.oran_pct}` : "—"})
        </span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-line">
        <div className="h-2 rounded-full bg-brand-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
